import { Formation, FormationPosition } from '@/components/team/FormationPitch';
import { Player } from '@/types';
import { generateFormation } from '@/data/formations';

interface WyscoutLineupPlayer {
  playerId: number;
  shirtNumber?: number;
  position: string; // e.g. "GK", "CB", "LB", "CM", "ST", "RW", etc.
}

const playerLabel = (shirtNumber: number | undefined): string => (
  shirtNumber === undefined ? 'Unknown player' : `#${shirtNumber}`
);

/** Classify a Wyscout position label into a formation role.
 * Handles both classic codes (CB, ST, CM) and formations API codes (RWF, LWF, DMF, LCMF3, AMF).
 */
function classifyRole(pos: string): FormationPosition['role'] {
  const p = pos.toUpperCase();
  if (p === 'GK') return 'GK';
  // Defenders: classic + formations API codes
  if (['CB', 'LCB', 'RCB', 'LB', 'RB', 'LWB', 'RWB', 'SW'].includes(p)) return 'DEF';
  // Forwards: classic + formations API codes (RWF=right wing forward, LWF=left wing forward, CF=center forward)
  if (['ST', 'CF', 'LW', 'RW', 'LST', 'RST', 'LF', 'RF', 'SS', 'RWF', 'LWF'].includes(p)) return 'FWD';
  // Everything else is midfield: CM, CDM, CAM, DMF, AMF, LCMF, RCMF, LCMF3, RCMF3, etc.
  return 'MID';
}

/** Assign a left-to-right order score based on position label prefix. */
function lateralScore(pos: string): number {
  const p = pos.toUpperCase();
  if (p.startsWith('L')) return 0;
  if (p.startsWith('R')) return 2;
  return 1; // center
}

/**
 * Generate a formation directly from the schema string.
 * No more fuzzy matching — any valid schema renders correctly.
 */
function findFormation(schema: string): { formation: Formation; formationName: string } | null {
  if (!schema) return null;
  const normalized = schema.replace(/\s+/g, '').trim();
  const formation = generateFormation(normalized);
  console.log(`[FormationMapper] Generated formation for "${normalized}": ${formation.positions.length} positions`);
  return { formation, formationName: normalized };
}

export interface MappingResult {
  formation: Formation;
  formationName: string;
  positionMap: Record<string, Player | undefined>;
  unmapped: WyscoutLineupPlayer[];
}

/**
 * Map a Wyscout lineup to a FormationPitch position map.
 *
 * @param schema        The formation schema string (e.g. "4-3-3")
 * @param lineup        Array of Wyscout lineup players
 * @param playerLookup  Map from wyscoutPlayerId -> internal Player object
 */
export function mapWyscoutLineup(
  schema: string,
  lineup: WyscoutLineupPlayer[],
  playerLookup: Map<number, Player>,
): MappingResult | null {
  console.log(`[FormationMapper] mapWyscoutLineup called: schema="${schema}", lineup=${lineup.length} players, lookup=${playerLookup.size} entries`);
  console.log(`[FormationMapper] Lineup positions:`, lineup.map((l) => `${l.position}(#${l.shirtNumber})`).join(', '));

  const found = findFormation(schema);
  if (!found) {
    console.error(`[FormationMapper] findFormation returned null for schema "${schema}" - falling back to first formation`);
    // Fall back to first formation so we at least show something
    return mapWyscoutLineup('4-3-3', lineup, playerLookup);
  }

  const { formation, formationName } = found;
  const positionMap: Record<string, Player | undefined> = {};
  const used = new Set<number>(); // track which lineup players are assigned

  // Group formation positions by role
  const positionsByRole = new Map<FormationPosition['role'], FormationPosition[]>();
  for (const pos of formation.positions) {
    const group = positionsByRole.get(pos.role) || [];
    group.push(pos);
    positionsByRole.set(pos.role, group);
  }

  // Group lineup players by role
  const playersByRole = new Map<FormationPosition['role'], WyscoutLineupPlayer[]>();
  for (const lp of lineup) {
    const role = classifyRole(lp.position);
    const group = playersByRole.get(role) || [];
    group.push(lp);
    playersByRole.set(role, group);
  }

  console.log(`[FormationMapper] Role grouping:`);
  for (const role of ['GK', 'DEF', 'MID', 'FWD'] as FormationPosition['role'][]) {
    const posCount = (positionsByRole.get(role) || []).length;
    const playerCount = (playersByRole.get(role) || []).length;
    console.log(`  ${role}: ${posCount} positions, ${playerCount} players`);
  }

  // For each role, match players to positions by lateral order
  for (const role of ['GK', 'DEF', 'MID', 'FWD'] as FormationPosition['role'][]) {
    const positions = positionsByRole.get(role) || [];
    const players = playersByRole.get(role) || [];

    // Sort positions left to right by xPercent
    const sortedPositions = [...positions].sort((a, b) => a.xPercent - b.xPercent);
    // Sort players left to right by their position label
    const sortedPlayers = [...players].sort((a, b) => lateralScore(a.position) - lateralScore(b.position));

    // Match by index after sorting
    for (let i = 0; i < sortedPositions.length && i < sortedPlayers.length; i++) {
      const pos = sortedPositions[i];
      const lp = sortedPlayers[i];
      const player = playerLookup.get(lp.playerId);
      if (player) {
        positionMap[pos.id] = player;
        console.log(`  Mapped ${pos.id} (${pos.label}) -> ${player.name} (wyscout:${lp.playerId})`);
      } else {
        // Create synthetic player
        const synth: Player = {
          id: `wyscout-${lp.playerId}`,
          name: playerLabel(lp.shirtNumber),
          age: 0,
          position: roleToPosition(role),
          createdBy: '',
          createdAt: '',
          updatedAt: '',
          wyscoutPlayerId: lp.playerId,
        };
        positionMap[pos.id] = synth;
        console.log(`  Mapped ${pos.id} (${pos.label}) -> SYNTHETIC #${lp.shirtNumber} (wyscout:${lp.playerId})`);
      }
      used.add(lp.playerId);
    }

    // If we have more players than positions (role mismatch), they'll be unmapped
    if (sortedPlayers.length > sortedPositions.length) {
      console.warn(`  ${role}: ${sortedPlayers.length - sortedPositions.length} extra players couldn't be mapped`);
    }
    // If we have more positions than players, try to fill from overflow of other roles
    if (sortedPositions.length > sortedPlayers.length) {
      console.warn(`  ${role}: ${sortedPositions.length - sortedPlayers.length} positions left empty`);
    }
  }

  const unmapped = lineup.filter((lp) => !used.has(lp.playerId));
  if (unmapped.length > 0) {
    console.warn(`[FormationMapper] ${unmapped.length} unmapped players:`, unmapped.map((u) => `#${u.shirtNumber} (${u.position})`));

    // Try to fill empty positions with unmapped players
    const emptyPositions = formation.positions.filter((pos) => !positionMap[pos.id]);
    for (let i = 0; i < emptyPositions.length && i < unmapped.length; i++) {
      const pos = emptyPositions[i];
      const lp = unmapped[i];
      const player = playerLookup.get(lp.playerId);
      if (player) {
        positionMap[pos.id] = player;
      } else {
        positionMap[pos.id] = {
          id: `wyscout-${lp.playerId}`,
          name: playerLabel(lp.shirtNumber),
          age: 0,
          position: roleToPosition(pos.role),
          createdBy: '',
          createdAt: '',
          updatedAt: '',
          wyscoutPlayerId: lp.playerId,
        };
      }
      console.log(`  Overflow: mapped ${pos.id} (${pos.label}) -> #${lp.shirtNumber} (${lp.position})`);
    }
  }

  const mappedCount = Object.values(positionMap).filter(Boolean).length;
  console.log(`[FormationMapper] Result: ${mappedCount}/${formation.positions.length} positions filled, formation="${formationName}"`);

  return { formation, formationName, positionMap, unmapped };
}

function roleToPosition(role: FormationPosition['role']): Player['position'] {
  switch (role) {
    case 'GK': return 'Goalkeeper';
    case 'DEF': return 'Defender';
    case 'MID': return 'Midfielder';
    case 'FWD': return 'Forward';
  }
}
